import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { checkCheckoutStatus } from "../lib/api";

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | expired | error
  const [data, setData] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sid) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      tries.current += 1;
      try {
        const r = await checkCheckoutStatus(sid);
        setData(r);
        if (r.payment_status === "paid" || r.status === "complete") { setStatus("paid"); return; }
        if (r.status === "expired") { setStatus("expired"); return; }
        if (tries.current >= 25) { setStatus("error"); return; }
        timer = setTimeout(poll, 2000);
      } catch {
        if (tries.current >= 25) setStatus("error");
        else timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sid]);

  return (
    <div data-testid="booking-success-page" className="min-h-screen pt-32 pb-20 px-6 flex items-center justify-center bg-[#050505]">
      <div className="card-flat p-12 max-w-lg w-full text-center">
        {status === "polling" && (
          <>
            <Loader2 className="text-gold mx-auto mb-6 animate-spin" size={48} />
            <h1 className="font-serif text-3xl text-white mb-3">Confirming your booking</h1>
            <p className="text-white/55 text-sm">We're verifying the payment with Stripe. This usually takes a few seconds…</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="text-emerald mx-auto mb-6" size={56} />
            <p className="overline mb-4 text-emerald">Booking Confirmed</p>
            <h1 className="font-serif text-4xl text-white mb-4">Thank you.</h1>
            <p className="text-white/65 text-sm leading-relaxed mb-8">
              Your reservation is confirmed. A confirmation email is on the way and our concierge will be in touch with check-in details within the hour.
            </p>
            {data && (
              <div className="border-y border-white/10 py-5 mb-8 text-left text-sm">
                <div className="flex justify-between text-white/65 mb-2"><span>Amount paid</span><span className="text-gold font-serif text-lg">£{Number(data.amount).toLocaleString()}</span></div>
                <div className="flex justify-between text-white/65"><span>Currency</span><span className="uppercase">{data.currency}</span></div>
              </div>
            )}
            <Link to="/" data-testid="success-home-btn" className="btn-gold w-full justify-center">Return Home</Link>
          </>
        )}
        {(status === "expired" || status === "error") && (
          <>
            <XCircle className="text-red-400 mx-auto mb-6" size={48} />
            <h1 className="font-serif text-3xl text-white mb-3">Verification taking longer than expected</h1>
            <p className="text-white/60 text-sm mb-6 leading-relaxed">
              Your payment may still have succeeded — Stripe and our system sometimes take a minute to sync.
              If you were charged, your booking is recorded and our concierge will confirm via email or WhatsApp shortly.
            </p>
            <p className="text-white/40 text-xs mb-8">Reference: <span className="font-mono text-gold/70">{sid?.slice(-12)}</span></p>
            <a data-testid="success-whatsapp-btn" href={`https://wa.me/447474759947?text=${encodeURIComponent('Hi WLS — I just booked but the confirmation is taking a moment. Reference: ' + (sid || ''))}`} target="_blank" rel="noreferrer" className="btn-emerald w-full justify-center mb-3">Message Concierge on WhatsApp</a>
            <Link to="/contact" data-testid="success-contact-btn" className="btn-outline-gold w-full justify-center">Contact Form</Link>
          </>
        )}
      </div>
    </div>
  );
}
